import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Link2,
  RefreshCw,
  CalendarDays,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { useAuthStore } from '@/lib/auth-store'
import { cn } from '@/lib/utils'
import { AsyncBoundary } from '@/components/AsyncBoundary'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(
  /\/$/,
  '',
)

type CalendarEventType =
  | 'show'
  | 'rehearsal'
  | 'hotel'
  | 'transport'
  | 'muster'
  | 'travel'

interface CalendarEvent {
  id: string
  type: CalendarEventType
  title: string
  start: string
  end: string | null
  allDay: boolean
  location: string | null
  description: string | null
  tourId: string | null
  showId: string | null
}

interface TourRow {
  id: string
  name: string
  startDate?: string | null
  endDate?: string | null
}

interface CalendarFeed {
  id: string
  tourId: string
  token: string
  isActive: boolean
  rotatedAt: string | null
  tourName?: string
  subscribeUrl?: string
  webcalUrl?: string
}

const EVENT_COLOUR: Record<CalendarEventType, string> = {
  show: 'bg-indigo-500',
  rehearsal: 'bg-amber-500',
  hotel: 'bg-teal-500',
  transport: 'bg-sky-500',
  muster: 'bg-violet-500',
  travel: 'bg-rose-500',
}

const EVENT_LABEL: Record<CalendarEventType, string> = {
  show: 'Show',
  rehearsal: 'Rehearsal',
  hotel: 'Hotel',
  transport: 'Shuttle',
  muster: 'Muster',
  travel: 'Travel',
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function monthGrid(year: number, monthIdx: number): (string | null)[] {
  const firstDay = new Date(year, monthIdx, 1).getDay()
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate()
  const cells: (string | null)[] = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const mm = String(monthIdx + 1).padStart(2, '0')
    const dd = String(d).padStart(2, '0')
    cells.push(`${year}-${mm}-${dd}`)
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function eventOnDate(event: CalendarEvent, isoDate: string): boolean {
  const dayStart = new Date(`${isoDate}T00:00:00`)
  const dayEnd = new Date(`${isoDate}T23:59:59.999`)
  const start = new Date(event.start)
  const end = event.end ? new Date(event.end) : start
  if (event.allDay) {
    const startDay = event.start.slice(0, 10)
    const endDay = (event.end ?? event.start).slice(0, 10)
    return isoDate >= startDay && isoDate <= endDay
  }
  return start <= dayEnd && end >= dayStart
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function buildFeedUrls(token: string) {
  const subscribeUrl = `${API_BASE_URL}/public/calendar/${token}.ics`
  return {
    subscribeUrl,
    webcalUrl: subscribeUrl.replace(/^https?:/, 'webcal:'),
  }
}

export function PerformanceCalendar() {
  const qc = useQueryClient()
  const activePerf = useAuthStore((s) => s.activePerformance)
  const [viewDate, setViewDate] = useState(() => new Date())
  const [selectedTourId, setSelectedTourId] = useState<string | 'all'>(
    activePerf?.kind === 'tour' ? activePerf.id : 'all',
  )
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [copied, setCopied] = useState<'https' | 'webcal' | null>(null)
  const [rotating, setRotating] = useState(false)

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const monthLabel = viewDate.toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  })

  const rangeFrom = useMemo(() => {
    const d = new Date(year, month, 1)
    d.setDate(d.getDate() - 7)
    return d
  }, [year, month])

  const rangeTo = useMemo(() => {
    const d = new Date(year, month + 1, 0)
    d.setDate(d.getDate() + 7)
    return d
  }, [year, month])

  const toursQ = useQuery<TourRow[], ApiClientError>({
    queryKey: ['calendar-tours'],
    queryFn: () => artistFetch<TourRow[]>('/tours'),
  })

  const eventsQ = useQuery<CalendarEvent[], ApiClientError>({
    queryKey: [
      'calendar-events',
      selectedTourId,
      rangeFrom.toISOString(),
      rangeTo.toISOString(),
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        from: rangeFrom.toISOString(),
        to: rangeTo.toISOString(),
      })
      if (selectedTourId !== 'all') params.set('tourId', selectedTourId)
      return artistFetch<CalendarEvent[]>(`/calendar/events?${params}`)
    },
  })

  const feedQ = useQuery<CalendarFeed, ApiClientError>({
    queryKey: ['calendar-feed', selectedTourId],
    queryFn: () =>
      artistFetch<CalendarFeed>(`/tours/${selectedTourId}/calendar-feed`),
    enabled: selectedTourId !== 'all',
  })

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const event of eventsQ.data ?? []) {
      const cells = monthGrid(year, month)
      for (const iso of cells) {
        if (!iso) continue
        if (eventOnDate(event, iso)) {
          const list = map.get(iso) ?? []
          list.push(event)
          map.set(iso, list)
        }
      }
    }
    return map
  }, [eventsQ.data, year, month])

  const selectedDayEvents = selectedDay
    ? (eventsByDate.get(selectedDay) ?? []).sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
      )
    : []

  const feedUrls =
    feedQ.data?.subscribeUrl && feedQ.data.webcalUrl
      ? {
          subscribeUrl: feedQ.data.subscribeUrl,
          webcalUrl: feedQ.data.webcalUrl,
        }
      : feedQ.data?.token
        ? buildFeedUrls(feedQ.data.token)
        : null

  async function rotateFeedToken() {
    if (selectedTourId === 'all') return
    if (
      !window.confirm(
        'Generate a new subscription link? Anyone using the old link will stop receiving updates.',
      )
    ) {
      return
    }
    setRotating(true)
    try {
      await artistFetch(`/tours/${selectedTourId}/calendar-feed`, {
        method: 'PATCH',
        body: { rotateToken: true },
      })
      qc.invalidateQueries({ queryKey: ['calendar-feed', selectedTourId] })
    } catch (err) {
      window.alert(
        err instanceof ApiClientError ? err.message : 'Failed to rotate link.',
      )
    } finally {
      setRotating(false)
    }
  }

  function copyLink(kind: 'https' | 'webcal') {
    if (!feedUrls) return
    const text =
      kind === 'webcal' ? feedUrls.webcalUrl : feedUrls.subscribeUrl
    navigator.clipboard.writeText(text).catch(() => undefined)
    setCopied(kind)
    setTimeout(() => setCopied(null), 2000)
  }

  const todayIso = toIsoDate(new Date())
  const cells = monthGrid(year, month)

  return (
    <section className="rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl">
            <CalendarDays className="h-5 w-5" />
            Performance calendar
          </h2>
          <p className="mt-1 text-sm text-ovation-muted">
            Shows, rehearsals, hotels, and travel for your tour schedule.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="calendar-tour">
            Tour
          </label>
          <select
            id="calendar-tour"
            value={selectedTourId}
            onChange={(e) => {
              setSelectedTourId(e.target.value)
              setSelectedDay(null)
            }}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
          >
            <option value="all">All tours</option>
            {(toursQ.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <div className="flex items-center rounded-md border border-ovation-ink/15">
            <button
              type="button"
              onClick={() =>
                setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
              }
              className="px-2 py-1.5 hover:bg-ovation-ink/5"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[9rem] px-2 text-center text-sm font-medium">
              {monthLabel}
            </span>
            <button
              type="button"
              onClick={() =>
                setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
              }
              className="px-2 py-1.5 hover:bg-ovation-ink/5"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-wider text-ovation-muted">
        {(Object.keys(EVENT_LABEL) as CalendarEventType[]).map((type) => (
          <span key={type} className="flex items-center gap-1">
            <span
              className={cn('h-2 w-2 rounded-full', EVENT_COLOUR[type])}
            />
            {EVENT_LABEL[type]}
          </span>
        ))}
      </div>

      <AsyncBoundary isLoading={eventsQ.isLoading} error={eventsQ.error}>
        <div className="overflow-x-auto">
          <div className="min-w-[280px]">
            <div className="mb-1 grid grid-cols-7 gap-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="py-1 text-center text-[10px] font-medium uppercase tracking-wider text-ovation-muted"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((iso, i) => {
                if (!iso) {
                  return <div key={`pad-${i}`} className="min-h-[4.5rem]" />
                }
                const dayEvents = eventsByDate.get(iso) ?? []
                const isToday = iso === todayIso
                const isSelected = iso === selectedDay
                return (
                  <button
                    type="button"
                    key={iso}
                    onClick={() => setSelectedDay(iso)}
                    className={cn(
                      'min-h-[4.5rem] rounded-md border p-1 text-left text-xs transition-colors',
                      isSelected
                        ? 'border-ovation-ink bg-ovation-ink/5'
                        : 'border-ovation-ink/10 hover:border-ovation-ink/25',
                      isToday && !isSelected && 'border-ovation-ink/30',
                    )}
                  >
                    <div
                      className={cn(
                        'mb-1 font-medium',
                        isToday && 'text-ovation-ink',
                      )}
                    >
                      {Number(iso.slice(8, 10))}
                    </div>
                    <div className="flex flex-wrap gap-0.5">
                      {dayEvents.slice(0, 4).map((ev) => (
                        <span
                          key={ev.id}
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            EVENT_COLOUR[ev.type],
                          )}
                          title={ev.title}
                        />
                      ))}
                      {dayEvents.length > 4 && (
                        <span className="text-[9px] text-ovation-muted">
                          +{dayEvents.length - 4}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {selectedDay && (
          <div className="mt-4 rounded-md border border-ovation-ink/10 bg-ovation-paper/30 p-4">
            <h3 className="text-sm font-medium">
              {new Date(`${selectedDay}T12:00:00`).toLocaleDateString(
                undefined,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
              )}
            </h3>
            {selectedDayEvents.length === 0 ? (
              <p className="mt-2 text-sm text-ovation-muted">
                No events on this day.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {selectedDayEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className="flex items-start gap-3 rounded-md bg-white px-3 py-2 text-sm"
                  >
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        EVENT_COLOUR[ev.type],
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{ev.title}</div>
                      <div className="text-xs text-ovation-muted">
                        {EVENT_LABEL[ev.type]}
                        {!ev.allDay && (
                          <>
                            {' · '}
                            {formatTime(ev.start)}
                            {ev.end && ` – ${formatTime(ev.end)}`}
                          </>
                        )}
                        {ev.allDay && ' · All day'}
                      </div>
                      {ev.location && (
                        <div className="truncate text-xs text-ovation-muted">
                          {ev.location}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </AsyncBoundary>

      {selectedTourId !== 'all' && (
        <div className="mt-5 border-t border-ovation-ink/10 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="flex items-center gap-1.5 text-sm font-medium">
                <Link2 className="h-4 w-4" />
                Subscribe in your calendar
              </h3>
              <p className="mt-0.5 text-xs text-ovation-muted">
                Per-tour link — paste into Google Calendar, Apple Calendar, or
                Outlook. Rotating the link invalidates old subscriptions.
              </p>
            </div>
            <button
              type="button"
              disabled={rotating || feedQ.isLoading}
              onClick={rotateFeedToken}
              className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5 disabled:opacity-50"
            >
              <RefreshCw
                className={cn('h-3 w-3', rotating && 'animate-spin')}
              />
              New link
            </button>
          </div>

          {feedQ.isLoading && (
            <p className="mt-2 text-xs text-ovation-muted">Loading feed…</p>
          )}

          {feedUrls && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md bg-ovation-paper/60 px-2 py-1.5 text-[11px]">
                  {feedUrls.subscribeUrl}
                </code>
                <button
                  type="button"
                  onClick={() => copyLink('https')}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs hover:bg-ovation-ink/5"
                >
                  <Copy className="h-3 w-3" />
                  {copied === 'https' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md bg-ovation-paper/60 px-2 py-1.5 text-[11px]">
                  {feedUrls.webcalUrl}
                </code>
                <button
                  type="button"
                  onClick={() => copyLink('webcal')}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs hover:bg-ovation-ink/5"
                >
                  <Copy className="h-3 w-3" />
                  {copied === 'webcal' ? 'Copied' : 'Apple / macOS'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
